/**
 * @file WarehouseContext.tsx
 * @module app/context/WarehouseContext
 * @description Provides selected-warehouse state and update actions so modules consistently scope data and operations to the active warehouse.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";
import { getKey, removeKey, setKey } from "../../Global/history";
import { warehouse as warehouseStrings } from "../../utils/storeString";
import APICallService from "../../api/apiCallService";
import { multipleWarehouse } from "../../api/apiEndPoints";
import { Admin, AllModules, AssistAdmin, WarehouseModule } from "../../utils/constants";
import { useAuth } from "../modules/auth";
import Method from "../../utils/methods";

export interface WarehouseItem {
  value: string;
  label: string;
  title: string;
  _id: string;
  defaultWarehouse?: boolean;
  lat?: number;
  lng?: number;
  district?: Array<{
    name: string;
    reference: string;
  }>;
}

type WarehouseContextProps = {
  selectedWarehouse: WarehouseItem | null;
  setSelectedWarehouse: (warehouse: WarehouseItem | null) => void;
  warehouseList: WarehouseItem[];
  loading: boolean;
  fetchWarehouseList: () => Promise<void>;
};

const initWarehouseContextState: WarehouseContextProps = {
  selectedWarehouse: null,
  setSelectedWarehouse: () => {},
  warehouseList: [],
  loading: false,
  fetchWarehouseList: async () => {},
};

const WarehouseContext = createContext<WarehouseContextProps>(
  initWarehouseContextState
);

export const useWarehouse = () => {
  return useContext(WarehouseContext);
};

interface WarehouseProviderProps {
  children: ReactNode;
}

export const WarehouseProvider: FC<WarehouseProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [selectedWarehouse, setSelectedWarehouseState] =
    useState<WarehouseItem | null>(null);
  const [warehouseList, setWarehouseList] = useState<WarehouseItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Initialize from localStorage
  useEffect(() => {
    const storedWarehouse: any = getKey(warehouseStrings.warehouseRef);
    if (storedWarehouse) {
      try {
        const parsed = JSON.parse(storedWarehouse);
        setSelectedWarehouseState(parsed);
      } catch (error) {
        console.error("Error parsing stored warehouse:", error);
      }
    }
  }, []);

  const setSelectedWarehouse = (warehouse: WarehouseItem | null) => {
    setSelectedWarehouseState(warehouse);
    if (warehouse) {
      setKey(warehouseStrings.warehouseRef, warehouse);
    } else {
      removeKey(warehouseStrings.warehouseRef);
    }
  };

  const fetchWarehouseList = async () => {
    const params: any = {
      skip: 0,
      sortKey: "name",
      sortOrder: 1,
      needCount: true,
    };

    setLoading(true);
    try {
      if (
        currentUser?.userType == Admin ||
        currentUser?.userType === AssistAdmin || 
        ((currentUser?.warehouse?.defaultWarehouse && Method.hasModulePermission(WarehouseModule, currentUser)))
      ) {
        const apiService = new APICallService(
          multipleWarehouse.listWarehouse,
          params,
          "",
          "",
          "",
          "",
          AllModules,
          ""
        );
        const response = await apiService.callAPI();
        const data: WarehouseItem[] = [];

        if (response.records.length) {
          response.records.forEach((val: any) => {
            data.push({
              value: val._id,
              label: val.name,
              title: val.name,
              _id: val._id,
              defaultWarehouse: val.defaultWarehouse,
              lat: val.lat,
              lng: val.lng,
              district: val.district,
            });
          });
        }
        setWarehouseList(data);
        let selectedWh: WarehouseItem | null = null;

        // get from localstorage
        const storedWarehouse: any = getKey(warehouseStrings.warehouseRef);
        if (storedWarehouse) {
          try {
            const parsed = JSON.parse(storedWarehouse);
            const found = data.find((item: any) => item._id === parsed._id);
            if (found) {
              selectedWh = found;
            }
          } catch (error) {
            console.error("Error parsing stored warehouse:", error);
          }
        }
        // If not in localStorage, get from currentUser
        if (!selectedWh && currentUser?.warehouse) {
          const userWarehouse: WarehouseItem = {
            value: currentUser.warehouse._id,
            label: currentUser.warehouse.name,
            title: currentUser.warehouse.name,
            _id: currentUser.warehouse._id,
            lat: currentUser.warehouse.lat,
            lng: currentUser.warehouse.lng,
          };
          selectedWh = userWarehouse;
          setKey(warehouseStrings.warehouseRef, userWarehouse);
        }

        if (selectedWh) {
          setSelectedWarehouseState(selectedWh);
        }
      } else {
        let selectedWh: WarehouseItem | null = null;

        if (currentUser?.warehouse) {
          const userWarehouse: WarehouseItem = {
            value: currentUser.warehouse._id,
            label: currentUser.warehouse.name,
            title: currentUser.warehouse.name,
            _id: currentUser.warehouse._id,
            defaultWarehouse: currentUser.warehouse.defaultWarehouse,
            lat: currentUser.warehouse.lat,
            lng: currentUser.warehouse.lng,
            district: currentUser.warehouse.district,
          };
          selectedWh = userWarehouse;
          setKey(warehouseStrings.warehouseRef, userWarehouse);
        }

        if (selectedWh) {
          setSelectedWarehouseState(selectedWh);
        }
      }
    } catch (error) {
      console.error("Error fetching warehouse list:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <WarehouseContext.Provider
      value={{
        selectedWarehouse,
        setSelectedWarehouse,
        warehouseList,
        loading,
        fetchWarehouseList,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};
